import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, Users, Plus, Edit2, Trash2, ArrowLeft, Home, FolderOpen } from 'lucide-react';
import { format, startOfWeek, addDays, isSameDay, parseISO } from 'date-fns';
import { supabase } from '@/api/supabaseClient';
import { createPageUrl } from '@/utils';
import InstallerManagement from '@/components/InstallerManagement';
import ScheduleInstallDialog from '@/components/ScheduleInstallDialog';
import BlockTimeDialog from '@/components/BlockTimeDialog';

export default function Schedule() {
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 })); // Monday
  const [installers, setInstallers] = useState([]);
  const [scheduledInstalls, setScheduledInstalls] = useState([]);
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [showInstallerManagement, setShowInstallerManagement] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedInstaller, setSelectedInstaller] = useState(null);

  const timeSlots = [
    '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM'
  ];

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  useEffect(() => {
    loadInstallers();
    loadScheduledInstalls();
    loadBlockedTimes();
  }, [currentWeekStart]);

  const loadInstallers = async () => {
    const { data, error } = await supabase
      .from('installers')
      .select('*')
      .order('name');
    
    if (error) {
      console.error('Error loading installers:', error);
      return;
    }
    
    setInstallers(data || []);
  };

  const loadScheduledInstalls = async () => {
    const weekEnd = addDays(currentWeekStart, 7);
    
    const { data, error } = await supabase
      .from('scheduled_installs')
      .select(`
        *,
        projects (
          id,
          name,
          customer_name
        ),
        installers (
          id,
          name,
          color
        )
      `)
      .gte('install_date', format(currentWeekStart, 'yyyy-MM-dd'))
      .lt('install_date', format(weekEnd, 'yyyy-MM-dd'));
    
    if (error) {
      console.error('Error loading scheduled installs:', error);
      return;
    }
    
    setScheduledInstalls(data || []);
  };

  const loadBlockedTimes = async () => {
    const weekEnd = addDays(currentWeekStart, 7);
    
    const { data, error } = await supabase
      .from('blocked_times')
      .select(`
        *,
        installers (
          id,
          name,
          color
        )
      `)
      .gte('block_date', format(currentWeekStart, 'yyyy-MM-dd'))
      .lt('block_date', format(weekEnd, 'yyyy-MM-dd'));
    
    if (error) {
      console.error('Error loading blocked times:', error);
      return;
    }
    
    setBlockedTimes(data || []);
  };

  const nextWeek = () => {
    setCurrentWeekStart(addDays(currentWeekStart, 7));
  };

  const prevWeek = () => {
    setCurrentWeekStart(addDays(currentWeekStart, -7));
  };

  const thisWeek = () => {
    setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
  };

  const getInstallsForDateAndInstaller = (date, installerId) => {
    return scheduledInstalls.filter(
      install => isSameDay(parseISO(install.install_date), date) && 
                 install.installer_id === installerId
    );
  };

  const getBlockedTimesForDateAndInstaller = (date, installerId) => {
    return blockedTimes.filter(
      block => isSameDay(parseISO(block.block_date), date) && 
               block.installer_id === installerId
    );
  };

  const isInstallerAvailable = (date, installerId) => {
    const installer = installers.find(i => i.id === installerId);
    if (!installer || !installer.recurring_availability) return true;
    
    const dayOfWeek = date.getDay();
    const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayOfWeek];
    
    return installer.recurring_availability[dayName] === true;
  };

  const handleCellClick = (date, installer) => {
    setSelectedDate(date);
    setSelectedInstaller(installer);
    setShowScheduleDialog(true);
  };

  const handleBlockTime = (date, installer) => {
    setSelectedDate(date);
    setSelectedInstaller(installer);
    setShowBlockDialog(true);
  };

  if (installers.length === 0) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Installation Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center py-12">
            <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Installers Yet</h3>
            <p className="text-slate-600 mb-4">Add yourself and your team members to start scheduling.</p>
            <Button onClick={() => setShowInstallerManagement(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Installers
            </Button>
          </CardContent>
        </Card>

        {showInstallerManagement && (
          <InstallerManagement
            onClose={() => {
              setShowInstallerManagement(false);
              loadInstallers();
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Navigation */}
      <div className="mb-6 flex items-center gap-4">
        <img 
          src="/SNO-LightingLogo.png" 
          alt="SNO-Lighting Logo" 
          className="h-10 w-auto"
        />
        <Link to={createPageUrl('Home')}>
          <Button variant="outline" size="sm">
            <Home className="w-4 h-4 mr-2" />
            Home
          </Button>
        </Link>
        <Link to={createPageUrl('Projects')}>
          <Button variant="outline" size="sm">
            <FolderOpen className="w-4 h-4 mr-2" />
            Projects
          </Button>
        </Link>
      </div>

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="w-6 h-6" />
            Installation Schedule
          </h1>
          <p className="text-slate-600 mt-1">
            Week of {format(currentWeekStart, 'MMMM d, yyyy')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={prevWeek}>← Previous</Button>
          <Button variant="outline" onClick={thisWeek}>This Week</Button>
          <Button variant="outline" onClick={nextWeek}>Next →</Button>
          <Button onClick={() => setShowInstallerManagement(true)}>
            <Users className="w-4 h-4 mr-2" />
            Manage Team
          </Button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="grid grid-cols-8 border-b bg-slate-50">
          <div className="p-3 font-semibold border-r">Installer</div>
          {weekDays.map(day => (
            <div key={day.toString()} className="p-3 text-center border-r last:border-r-0">
              <div className="font-semibold">{format(day, 'EEE')}</div>
              <div className="text-sm text-slate-600">{format(day, 'MMM d')}</div>
            </div>
          ))}
        </div>

        {installers.map(installer => (
          <div key={installer.id} className="grid grid-cols-8 border-b last:border-b-0">
            <div 
              className="p-3 font-medium border-r flex items-center gap-2"
              style={{ borderLeft: `4px solid ${installer.color || '#3b82f6'}` }}
            >
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: installer.color || '#3b82f6' }}
              />
              {installer.name}
            </div>
            {weekDays.map(day => {
              const installs = getInstallsForDateAndInstaller(day, installer.id);
              const blocks = getBlockedTimesForDateAndInstaller(day, installer.id);
              const isAvailable = isInstallerAvailable(day, installer.id);
              const isPast = day < new Date().setHours(0, 0, 0, 0);

              return (
                <div
                  key={day.toString()}
                  className={`p-2 border-r last:border-r-0 min-h-[120px] ${
                    isPast ? 'bg-slate-50' : 'cursor-pointer hover:bg-slate-50'
                  } ${!isAvailable && 'bg-slate-100 opacity-50'}`}
                  onClick={() => !isPast && handleCellClick(day, installer)}
                >
                  {!isAvailable && (
                    <div className="text-xs text-slate-500 mb-1">Day Off</div>
                  )}
                  
                  {installs.map(install => (
                    <div
                      key={install.id}
                      className="mb-1 p-2 rounded text-xs border-l-2"
                      style={{ 
                        borderLeftColor: installer.color || '#3b82f6',
                        backgroundColor: `${installer.color || '#3b82f6'}15`
                      }}
                    >
                      <div className="font-semibold truncate">
                        {install.projects?.name || 'Unknown Project'}
                      </div>
                      <div className="text-slate-600">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {install.start_time} ({install.estimated_hours}h)
                      </div>
                    </div>
                  ))}

                  {blocks.map(block => (
                    <div
                      key={block.id}
                      className="mb-1 p-2 rounded text-xs bg-slate-200 border-l-2 border-slate-400"
                    >
                      <div className="font-semibold">Blocked</div>
                      <div className="text-slate-600 text-xs">
                        {block.start_time} - {block.end_time}
                        {block.reason && `: ${block.reason}`}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {showInstallerManagement && (
        <InstallerManagement
          onClose={() => {
            setShowInstallerManagement(false);
            loadInstallers();
          }}
        />
      )}

      {showScheduleDialog && (
        <ScheduleInstallDialog
          date={selectedDate}
          installer={selectedInstaller}
          onClose={() => {
            setShowScheduleDialog(false);
            setSelectedDate(null);
            setSelectedInstaller(null);
            loadScheduledInstalls();
          }}
        />
      )}

      {showBlockDialog && (
        <BlockTimeDialog
          date={selectedDate}
          installer={selectedInstaller}
          onClose={() => {
            setShowBlockDialog(false);
            setSelectedDate(null);
            setSelectedInstaller(null);
            loadBlockedTimes();
          }}
        />
      )}
    </div>
  );
}
